import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import { pricesApi } from '@/api/endpoints/prices'
import { formatCurrency } from '@/shared/utils/format-currency'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { ResponsiveTableWrapper } from '@/shared/components/responsive-table-wrapper'
import { PermissionGate } from '@/shared/components/permission-gate'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'

export function PricesMatrixPage() {
  const queryClient = useQueryClient()
  const { t: tPricing } = useTranslation('pricing')
  const { t: tCommon } = useTranslation('common')
  const { can } = usePermissions()
  const canEdit = can(ACTIONS.PRICE_EDIT)
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [includeInactive, setIncludeInactive] = useState(false)
  const [page, setPage] = useState(0)
  const size = 50

  // Debounce search
  useEffect(() => {
    const h = setTimeout(() => setDebounced(search), 300)
    return () => clearTimeout(h)
  }, [search])

  type PricesMatrixResponse = Awaited<ReturnType<typeof pricesApi.matrix>>

  const { data, isLoading, isFetching, error } = useQuery<PricesMatrixResponse>(
    {
      queryKey: ['prices-matrix', debounced, includeInactive, page, size],
      queryFn: () =>
        pricesApi.matrix({
          search: debounced || undefined,
          includeInactive,
          page,
          size,
        }),
      placeholderData: (previous) => previous,
    },
  )

  const restaurants = data?.restaurants ?? []
  const items = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / size))

  const [editing, setEditing] = useState<{ key: string; value: string } | null>(
    null,
  )

  const setPrice = useMutation({
    mutationFn: async (args: {
      sku: string
      restaurantId: number
      amountCents: number
    }) => pricesApi.set(args.sku, args.restaurantId, args.amountCents),
    onSuccess: () => {
      toast.success(tPricing('toast.priceUpdated'))
      queryClient.invalidateQueries({ queryKey: ['prices-matrix'] })
      setEditing(null)
    },
    onError: () => toast.error(tPricing('toast.priceUpdateFailed')),
  })

  const handleEdit = (sku: string, restaurantId: number, current?: number) => {
    setEditing({
      key: `${sku}:${restaurantId}`,
      value: current !== undefined ? (current / 100).toFixed(2) : '',
    })
  }

  const handleSave = (sku: string, restaurantId: number) => {
    if (!editing) return
    const euros = parseFloat(editing.value)
    if (isNaN(euros) || euros < 0) {
      toast.error(tPricing('toast.invalidPrice'))
      return
    }
    const amountCents = Math.round(euros * 100)
    setPrice.mutate({ sku, restaurantId, amountCents })
  }

  const isEditing = (sku: string, restaurantId: number) =>
    editing?.key === `${sku}:${restaurantId}`

  const pagedLabel = useMemo(() => {
    const from = total === 0 ? 0 : page * size + 1
    const to = Math.min((page + 1) * size, total)
    return tPricing('matrix.pagedLabel', { from, to, total })
  }, [page, size, total, tPricing])

  return (
    <PermissionGate action={ACTIONS.PRICE_VIEW}>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">{tPricing('matrix.title')}</h1>
          <p className="text-muted-foreground mt-2">
            {tPricing('matrix.subtitle')}
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{tPricing('matrix.filtersTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 items-center">
              <div className="w-64">
                <Label htmlFor="search">{tPricing('matrix.searchLabel')}</Label>
                <Input
                  id="search"
                  placeholder={tPricing('matrix.searchPlaceholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="inactive"
                  checked={includeInactive}
                  onCheckedChange={setIncludeInactive}
                />
                <Label htmlFor="inactive">
                  {tPricing('matrix.includeInactive')}
                </Label>
              </div>
              <div className="ml-auto flex items-center gap-2 text-sm text-muted-foreground">
                <span>{pagedLabel}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0 || isFetching}
                >
                  {tPricing('matrix.prev')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setPage((p) => Math.min(totalPages - 1, p + 1))
                  }
                  disabled={page >= totalPages - 1 || isFetching}
                >
                  {tPricing('matrix.next')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-[400px] w-full" />
          </div>
        ) : error ? (
          <div className="text-destructive">
            {tPricing('matrix.loadFailed')}
          </div>
        ) : (
          <Card>
            <CardContent className="pt-6">
              <ResponsiveTableWrapper>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[140px]">
                        {tCommon('sku')}
                      </TableHead>
                      <TableHead className="min-w-[200px]">
                        {tCommon('name')}
                      </TableHead>
                      {restaurants.map((r) => (
                        <TableHead
                          key={r.id}
                          className="text-right whitespace-nowrap"
                        >
                          {r.code}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((row) => (
                      <TableRow key={row.sku}>
                        <TableCell className="font-mono">{row.sku}</TableCell>
                        <TableCell>{row.name}</TableCell>
                        {row.prices.map((p) => {
                          const current = p.amountCents
                          const editingCell = isEditing(row.sku, p.restaurantId)
                          return (
                            <TableCell
                              key={`${row.sku}-${p.restaurantId}`}
                              className="text-right"
                            >
                              {editingCell ? (
                                <div className="flex justify-end gap-2">
                                  <Input
                                    value={editing?.value || ''}
                                    onChange={(e) =>
                                      setEditing({
                                        key: editing!.key,
                                        value: e.target.value,
                                      })
                                    }
                                    type="number"
                                    step="0.10"
                                    min="0"
                                    className="w-24"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter')
                                        handleSave(row.sku, p.restaurantId)
                                      if (e.key === 'Escape') setEditing(null)
                                    }}
                                  />
                                  <Button
                                    size="sm"
                                    onClick={() =>
                                      handleSave(row.sku, p.restaurantId)
                                    }
                                    disabled={setPrice.isPending || !canEdit}
                                  >
                                    {tCommon('save')}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setEditing(null)}
                                    disabled={setPrice.isPending || !canEdit}
                                  >
                                    {tCommon('cancel')}
                                  </Button>
                                </div>
                              ) : current !== undefined ? (
                                <button
                                  className="font-mono hover:underline"
                                  onClick={() =>
                                    handleEdit(row.sku, p.restaurantId, current)
                                  }
                                  disabled={!canEdit}
                                >
                                  {formatCurrency(current)}
                                </button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    handleEdit(row.sku, p.restaurantId)
                                  }
                                  disabled={!canEdit}
                                >
                                  {tPricing('matrix.set')}
                                </Button>
                              )}
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ResponsiveTableWrapper>
            </CardContent>
          </Card>
        )}
      </div>
    </PermissionGate>
  )
}
